import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  Tag,
  Tabs,
  Table,
  Tooltip,
  Progress,
  Empty,
  Button,
  Collapse,
  Spin,
  Select,
  Input,
  Pagination,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import {
  TrophyOutlined,
  CheckCircleFilled,
  ArrowUpOutlined,
  TeamOutlined,
  StarOutlined,
  DollarCircleOutlined,
  AimOutlined,
  RiseOutlined,
  WarningFilled,
  CheckCircleOutlined,
  CloseCircleFilled,
  QuestionCircleOutlined,
  BulbFilled,
  ThunderboltFilled,
  FullscreenOutlined,
  FullscreenExitOutlined,
  FileMarkdownOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { motion } from "framer-motion";
import agentService from "../../../api/agent/agentService";
import MarkdownMessage from "./components/MarkdownMessage";
import { DocViewerProvider } from "./components/askai/DocViewer";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import RegenerateLoader from "./components/RegenerateLoader";

interface Props {
  tenderID?: string;
  tenderData?: any;
}

// Last-line safety: if any tab's render throws, show an Empty state in place
// of the tab rather than letting the whole page crash.
class TabErrorBoundary extends React.Component<
  { children: React.ReactNode; label?: string },
  { error: Error | null }
> {
  constructor(props: any) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error) {
    console.error("AIInsightView tab render error:", error);
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: "30px 0" }}>
          <Empty
            description={
              <span style={{ color: "#64748b" }}>
                Couldn't render {this.props.label || "this view"} — the response
                may be missing some fields.
              </span>
            }
          />
        </div>
      );
    }
    return this.props.children;
  }
}

// ---- Helpers ----
// isFinite(null) is true (Number(null) === 0), so we have to reject
// null/undefined/non-numeric explicitly before the finiteness check.
const toFiniteNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const n = typeof value === "string" ? parseFloat(value) : (value as number);
  return typeof n === "number" && isFinite(n) ? n : null;
};

const formatCr = (value: number | string | null | undefined) => {
  const n = toFiniteNumber(value);
  if (n === null) return "—";
  return `₹${(n / 1e7).toFixed(2)} Cr`;
};

// JSX variant of formatCr that renders the "Cr" unit in a smaller, lighter
// span so big numbers in dashboard tiles don't look like the unit is fighting
// the figure for attention. Use this anywhere the value sits in a large font.
const formatCrJSX = (value: number | string | null | undefined): React.ReactNode => {
  const n = toFiniteNumber(value);
  if (n === null) return "—";
  return (
    <>
      ₹{(n / 1e7).toFixed(2)}
      <span
        style={{
          fontSize: "0.6em",
          marginLeft: 3,
          fontWeight: 600,
          opacity: 0.75,
          letterSpacing: 0.2,
        }}
      >
        Cr
      </span>
    </>
  );
};
const formatNum = (value: number | string | null | undefined, decimals = 2) => {
  const n = toFiniteNumber(value);
  if (n === null) return "—";
  return n.toLocaleString("en-IN", { maximumFractionDigits: decimals });
};
const formatPct = (value: number | string | null | undefined, decimals = 2) => {
  const n = toFiniteNumber(value);
  if (n === null) return "—";
  return `${n.toFixed(decimals)}%`;
};

// Rating presentation for supplier-history v2 dimensions
const ratingMeta = (rating?: string) => {
  switch (rating) {
    case "good":
      return { color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0", label: "Good", icon: <CheckCircleOutlined /> };
    case "average":
      return { color: "#d97706", bg: "#fffbeb", border: "#fde68a", label: "Average", icon: <BulbFilled /> };
    case "concern":
      return { color: "#dc2626", bg: "#fef2f2", border: "#fecaca", label: "Concern", icon: <CloseCircleFilled /> };
    case "insufficient_data":
    default:
      return { color: "#6b7280", bg: "#f3f4f6", border: "#e5e7eb", label: "No data", icon: <QuestionCircleOutlined /> };
  }
};

const verdictColor = (verdict?: string) => {
  const v = (verdict || "").toLowerCase();
  if (v.includes("avoid") || v.includes("reject")) return "red";
  if (v.includes("caution")) return "orange";
  if (v.includes("recommend") || v.includes("strong")) return "green";
  return "blue";
};

// Minimal markdown renderer (headings, bold, tables, lists, paragraphs)
const renderMarkdown = (md: string) => {
  const lines = md.split("\n");
  const blocks: React.ReactNode[] = [];
  let i = 0;
  let key = 0;

  const inline = (text: string): React.ReactNode => {
    const parts: React.ReactNode[] = [];
    const remaining = text;
    let idx = 0;
    const boldRe = /\*\*(.+?)\*\*/g;
    let last = 0;
    let m: RegExpExecArray | null;
    while ((m = boldRe.exec(remaining))) {
      if (m.index > last) parts.push(remaining.slice(last, m.index));
      parts.push(<strong key={idx++}>{m[1]}</strong>);
      last = m.index + m[0].length;
    }
    if (last < remaining.length) parts.push(remaining.slice(last));
    return parts;
  };

  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) {
      i++;
      continue;
    }
    if (line.startsWith("## ")) {
      blocks.push(
        <h3
          key={key++}
          style={{ fontSize: 16, fontWeight: 700, color: "#1e3a8a", marginTop: 18, marginBottom: 8 }}
        >
          {line.slice(3)}
        </h3>
      );
      i++;
    } else if (line.startsWith("### ")) {
      blocks.push(
        <h4
          key={key++}
          style={{ fontSize: 13, fontWeight: 700, color: "#1a1a1a", marginTop: 12, marginBottom: 6 }}
        >
          {line.slice(4)}
        </h4>
      );
      i++;
    } else if (line.trim().startsWith("|") && lines[i + 1]?.trim().startsWith("|")) {
      const tableLines: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) {
        tableLines.push(lines[i]);
        i++;
      }
      const headerRow = tableLines[0].split("|").slice(1, -1).map((c) => c.trim());
      const bodyRows = tableLines
        .slice(2)
        .map((row) => row.split("|").slice(1, -1).map((c) => c.trim()));
      blocks.push(
        <div key={key++} style={{ overflowX: "auto", marginBottom: 12 }}>
          <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "#f5f8ff" }}>
                {headerRow.map((h, hi) => (
                  <th
                    key={hi}
                    style={{
                      border: "1px solid #e6e8ef",
                      padding: "6px 10px",
                      textAlign: "left",
                      color: "#1e3a8a",
                      fontWeight: 600,
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bodyRows.map((row, ri) => (
                <tr key={ri}>
                  {row.map((c, ci) => (
                    <td
                      key={ci}
                      style={{ border: "1px solid #eef0f3", padding: "6px 10px", color: "#333" }}
                    >
                      {inline(c)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    } else if (line.trim().startsWith("- ")) {
      const items: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith("- ")) {
        items.push(lines[i].trim().slice(2));
        i++;
      }
      blocks.push(
        <ul key={key++} style={{ paddingLeft: 22, marginBottom: 10, fontSize: 13 }}>
          {items.map((it, ii) => (
            <li key={ii} style={{ marginBottom: 4, color: "#333" }}>
              {inline(it)}
            </li>
          ))}
        </ul>
      );
    } else {
      blocks.push(
        <p key={key++} style={{ fontSize: 13, color: "#333", lineHeight: 1.6, marginBottom: 10 }}>
          {inline(line)}
        </p>
      );
      i++;
    }
  }
  return blocks;
};

// ---- Component ----
const AIInsightView: React.FC<Props> = ({ tenderID, tenderData }) => {
  const [innerTab, setInnerTab] = useState("ranked");
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [regenerating, setRegenerating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [negotiationSupplier, setNegotiationSupplier] = useState<string | null>(null);
  const [reportFullscreen, setReportFullscreen] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const fullscreenReportRef = React.useRef<HTMLDivElement>(null);

  // Render the fullscreen report into a single tall canvas, then split it
  // across A4 pages — but instead of slicing at fixed pixel boundaries
  // (which cuts text), scan upward from each natural page break to find a
  // near-empty horizontal band (the gap between paragraphs / table rows /
  // section headings) and slice there. Result: no glyphs are bisected.
  const handleDownloadPdf = useCallback(async () => {
    const el = fullscreenReportRef.current;
    if (!el) return;

    // Flip loading state first so React commits any pending re-render BEFORE
    // we mutate inline styles. Otherwise React would re-render mid-capture
    // and overwrite our style mutations, leaving the chips empty in the PDF.
    setPdfDownloading(true);
    // Two RAFs is enough for React 18 to flush the render and the browser
    // to paint the loading spinner.
    await new Promise<void>((r) => requestAnimationFrame(() => r()));
    await new Promise<void>((r) => requestAnimationFrame(() => r()));

    // We mutate the live element and restore each mutation in finally.
    type Restore = () => void;
    const restorers: Restore[] = [];

    const stash = (node: HTMLElement, prop: keyof CSSStyleDeclaration) => {
      const prev = node.style[prop] as string;
      restorers.push(() => {
        (node.style as any)[prop] = prev;
      });
    };
    const setAndStash = (
      node: HTMLElement,
      prop: keyof CSSStyleDeclaration,
      value: string
    ) => {
      stash(node, prop);
      (node.style as any)[prop] = value;
    };

    // Convert every source chip to plain blue text in the PDF — no pill,
    // no icon, no download arrow. html2canvas has a hard time rendering the
    // pill's max-width + ellipsis combo, and the user prefers the simpler
    // inline-link presentation for print.
    el.querySelectorAll<HTMLElement>(".askai-md-source-chip").forEach((n) => {
      setAndStash(n, "background", "transparent");
      setAndStash(n, "border", "none");
      setAndStash(n, "padding", "0");
      setAndStash(n, "margin", "0 2px");
      setAndStash(n, "borderRadius", "0");
      setAndStash(n, "color", "#2563eb");
      setAndStash(n, "textDecoration", "none");
      setAndStash(n, "lineHeight", "1.55");
    });
    // Drop the file icon (first span child) and the trailing download
    // arrow so only the filename + optional meta survive.
    el.querySelectorAll<HTMLElement>(
      ".askai-md-source-chip > span:first-child, .askai-md-source-chip .anticon"
    ).forEach((n) => setAndStash(n, "display", "none"));
    // Relax all inner spans (filename, meta) so the ellipsis doesn't blank
    // the text and the colour matches the link.
    el.querySelectorAll<HTMLElement>(".askai-md-source-chip span").forEach((n) => {
      setAndStash(n, "maxWidth", "none");
      setAndStash(n, "overflow", "visible");
      setAndStash(n, "textOverflow", "clip");
      setAndStash(n, "whiteSpace", "normal");
      setAndStash(n, "color", "#2563eb");
    });

    // Hide every TableCard toolbar by walking up from the Download CSV
    // button to its immediate parent (the toolbar row).
    el.querySelectorAll<HTMLButtonElement>("button").forEach((btn) => {
      const label = (btn.textContent || "").trim().toLowerCase();
      if (label.includes("download csv")) {
        const toolbar = btn.parentElement as HTMLElement | null;
        if (toolbar) setAndStash(toolbar, "display", "none");
      }
    });

    // Drop the inner scroll cap on table containers so long tables render
    // fully in the PDF rather than being truncated at maxHeight: 360px.
    el.querySelectorAll<HTMLElement>("div").forEach((d) => {
      if (
        d.style.overflow === "auto" &&
        d.style.maxHeight &&
        /\d+px/.test(d.style.maxHeight)
      ) {
        setAndStash(d, "maxHeight", "none");
        setAndStash(d, "overflow", "visible");
      }
    });

    try {
      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
      });
      const ctx = canvas.getContext("2d");

      const pdf = new jsPDF({
        orientation: "p",
        unit: "mm",
        format: "a4",
        compress: true,
      });
      const pageWidthMm = pdf.internal.pageSize.getWidth();
      const pageHeightMm = pdf.internal.pageSize.getHeight();
      const marginMm = 10;
      const contentWidthMm = pageWidthMm - marginMm * 2;
      const contentHeightMm = pageHeightMm - marginMm * 2;

      // Canvas pixels per mm at the target render width.
      const pxPerMm = canvas.width / contentWidthMm;
      const pagePx = Math.floor(contentHeightMm * pxPerMm);

      // Detect whether a 1-px-tall horizontal strip is "mostly white" — the
      // signature of a gap between rendered text/table rows.
      const isGapRow = (rowY: number): boolean => {
        if (!ctx) return false;
        try {
          const row = ctx.getImageData(0, rowY, canvas.width, 1).data;
          // Sample every 4th pixel for speed; row is RGBA so step = 16.
          let dark = 0;
          let samples = 0;
          for (let i = 0; i < row.length; i += 16) {
            samples += 1;
            const r = row[i],
              g = row[i + 1],
              b = row[i + 2];
            if (r < 245 || g < 245 || b < 245) dark += 1;
          }
          return samples > 0 && dark / samples < 0.01;
        } catch {
          return false;
        }
      };

      // For a desired page-bottom around `targetY`, look back up to 20% of
      // the page height for a contiguous run of gap rows and split there.
      const findSafeSplit = (startY: number, targetY: number): number => {
        const scanFloor = Math.max(startY + Math.floor(pagePx * 0.4), targetY - Math.floor(pagePx * 0.2));
        for (let y = targetY; y >= scanFloor; y--) {
          // Need at least 3 consecutive gap rows so we don't stop in the
          // line-leading whitespace inside a paragraph.
          if (isGapRow(y) && isGapRow(y - 1) && isGapRow(y - 2)) {
            return y;
          }
        }
        return targetY; // fallback — no clean break found, accept the slice
      };

      let currentY = 0;
      let pageNo = 0;
      while (currentY < canvas.height) {
        const remaining = canvas.height - currentY;
        let endY: number;
        if (remaining <= pagePx) {
          endY = canvas.height;
        } else {
          endY = findSafeSplit(currentY, currentY + pagePx);
        }
        const sliceHeight = endY - currentY;

        // Render this slice onto a per-page canvas so we can serialise it
        // without copying the entire master canvas.
        const slice = document.createElement("canvas");
        slice.width = canvas.width;
        slice.height = sliceHeight;
        const sctx = slice.getContext("2d");
        if (sctx) {
          sctx.drawImage(
            canvas,
            0,
            currentY,
            canvas.width,
            sliceHeight,
            0,
            0,
            canvas.width,
            sliceHeight
          );
        }
        const sliceData = slice.toDataURL("image/png");
        const sliceHeightMm = sliceHeight / pxPerMm;

        if (pageNo > 0) pdf.addPage();
        pdf.addImage(
          sliceData,
          "PNG",
          marginMm,
          marginMm,
          contentWidthMm,
          sliceHeightMm
        );

        currentY = endY;
        pageNo += 1;
      }

      const tenderTag =
        data?.header?.tender_number || data?.tender_id || "report";
      pdf.save(`AI-Report-${tenderTag}.pdf`);
    } catch (err: any) {
      console.error("PDF download failed", err);
    } finally {
      setPdfDownloading(false);
      // Restore every style we mutated above so the live UI is untouched.
      restorers.forEach((fn) => {
        try {
          fn();
        } catch {
          /* ignore — best effort */
        }
      });
    }
  }, [data]);
  const [supplierSearch, setSupplierSearch] = useState("");
  const [supplierPage, setSupplierPage] = useState(1);
  const SUPPLIER_PAGE_SIZE = 5;
  const [freshness, setFreshness] = useState<any>(null);

  // Pull the tenant-wide data-freshness timestamp once on mount so we can
  // show "Last updated at" under the Regenerate button. Re-fetch whenever a
  // regenerate completes so the badge stays current.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res: any = await agentService.getDataFreshness();
        if (!cancelled) setFreshness(res);
      } catch (err) {
        // Non-critical — just don't show the badge.
        console.warn("data-freshness fetch failed", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [data]);

  // Lock body scroll while the report overlay is open, and let Esc close it.
  useEffect(() => {
    if (!reportFullscreen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setReportFullscreen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [reportFullscreen]);

  // Agent gateway is keyed by the numeric tender id (route param). Fall back to
  // tenderData.tenderID only if the route param is missing.
  const tenderRef: string | number | undefined =
    tenderID || tenderData?.tenderID;

  // The /ai-analysis endpoint returns the latest cached report when present;
  // otherwise it runs the LLM cold (~30 s). The /history list is informational
  // (for a version picker); it's not part of the load flow per the spec.
  const fetchAnalysis = useCallback(
    async (force = false) => {
      if (!tenderRef) {
        setError("Missing tender id");
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        // Decide if this is going to be a cold run so we can show the right
        // wait message. The history endpoint is cheap; an empty list means no
        // cached analysis yet, so /ai-analysis will run cold (~30 s).
        if (!force) {
          try {
            const hist: any = await agentService.getAnalysisHistory(tenderRef, 1);
            const n =
              hist?.n_versions ??
              (Array.isArray(hist?.versions) ? hist.versions.length : 0);
            setRegenerating(!n);
          } catch {
            // history is non-critical — proceed without it.
          }
        } else {
          setRegenerating(true);
        }

        const result: any = force
          ? await agentService.regenerateAnalysis(tenderRef)
          : await agentService.getAIAnalysis(tenderRef);

        const payload = result?.response ?? result?.data ?? result;
        if (!payload || !payload.tender_id) {
          throw new Error(
            payload?.detail?.message ||
              payload?.message ||
              "Empty AI analysis response"
          );
        }
        setData(payload);
      } catch (err: any) {
        console.error("AI analysis fetch failed", err);
        setError(
          err?.detail?.message ||
            err?.message ||
            "Failed to load AI analysis"
        );
        setData(null);
      } finally {
        setRegenerating(false);
        setLoading(false);
      }
    },
    [tenderRef]
  );

  const handleRegenerate = useCallback(() => {
    fetchAnalysis(true);
  }, [fetchAnalysis]);

  useEffect(() => {
    fetchAnalysis();
  }, [fetchAnalysis]);

  const headroom = data?.highest_lowest_price_spread || {};
  const scorecard = data?.compliance_scorecard || {};
  const pricing = data?.pricing_analysis || {};
  const historyV2 = data?.supplier_history_v2 || {};

  const kpiCards = useMemo(() => {
    if (!data) return [] as any[];
    // The agent puts the explicit lowest_bid / highest_bid blocks (with
    // supplier names) at either the top level or inside `stats`. Check both,
    // and as a last resort fall back to the older value_range numbers — which
    // don't include supplier identity.
    const lowestBid =
      data.lowest_bid ?? data.stats?.lowest_bid ?? null;
    const highestBid =
      data.highest_bid ?? data.stats?.highest_bid ?? null;
    const lowVal =
      lowestBid?.value ?? data.stats?.value_range?.min_gross_value;
    const highVal =
      highestBid?.value ?? data.stats?.value_range?.max_gross_value;
    // Walk a few likely places for the supplier name (some payloads use
    // bidder_name / supplier_name instead of supplier).
    const supplierOf = (b: any) =>
      b?.supplier || b?.supplier_name || b?.bidder_name || b?.bid_number;
    const lowSupplier = supplierOf(lowestBid);
    const highSupplier = supplierOf(highestBid);

    // Prefer the agent's price_spread block — it carries the official
    // amount_pct number along with the low/high anchors and bidder count.
    const priceSpread =
      data.price_spread ?? data.stats?.price_spread ?? null;
    const spreadAbs =
      priceSpread?.amount ??
      (lowVal != null && highVal != null
        ? Number(highVal) - Number(lowVal)
        : null);
    const spreadPct =
      priceSpread?.amount_pct ??
      (spreadAbs != null && Number(lowVal) > 0
        ? (spreadAbs / Number(lowVal)) * 100
        : null);
    const nBidders = priceSpread?.n_bidders;

    return [
      {
        label: "Bids Received",
        value: data.stats?.n_bids ?? "—",
        icon: <TeamOutlined />,
        accent: "#2563eb",
        bg: "#eff6ff",
        border: "#bfdbfe",
      },
      {
        label: "Top Score",
        value:
          data.stats?.score_range?.max_score != null
            ? `${data.stats.score_range.max_score.toFixed(1)}%`
            : "—",
        icon: <StarOutlined />,
        accent: "#a16207",
        bg: "#fef3c7",
        border: "#fde68a",
      },
      {
        label: "Lowest Bid",
        value: formatCrJSX(lowVal),
        subValue: lowSupplier,
        icon: <DollarCircleOutlined />,
        accent: "#0d9488",
        bg: "#ccfbf1",
        border: "#99f6e4",
      },
      {
        label: "Highest Bid",
        value: formatCrJSX(highVal),
        subValue: highSupplier,
        icon: <DollarCircleOutlined />,
        accent: "#dc2626",
        bg: "#fef2f2",
        border: "#fecaca",
      },
      {
        label: "Spread",
        value:
          spreadPct != null ? `${Number(spreadPct).toFixed(2)}%` : "—",
        subValue:
          spreadAbs != null
            ? `${formatCr(spreadAbs)}${
                nBidders ? ` · ${nBidders} bidders` : ""
              }`
            : undefined,
        icon: <AimOutlined />,
        accent: "#7c3aed",
        bg: "#ede9fe",
        border: "#ddd6fe",
      },
      {
        label: "Negotiation Headroom",
        value: formatCrJSX(headroom.total_savings_potential || 0),
        icon: <RiseOutlined />,
        accent: "#16a34a",
        bg: "#f0fdf4",
        border: "#bbf7d0",
        tag: {
          text: `${headroom.n_lines_with_headroom || 0} lines`,
          color: "#16a34a",
        },
      },
    ];
  }, [data, headroom]);

  // ---------------------- Ranked Bids ----------------------
  const rankedColumns = [
    {
      title: "Rank",
      dataIndex: "rank",
      key: "rank",
      width: 70,
      align: "center" as const,
      render: (r: number) => (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 26,
            height: 26,
            borderRadius: "50%",
            background:
              r === 1 ? "#fef3c7" : r === 2 ? "#f3f4f6" : r === 3 ? "#ffedd5" : "#f5f5f5",
            color: r === 1 ? "#a16207" : r === 2 ? "#6b7280" : r === 3 ? "#c2410c" : "#999",
            fontWeight: 700,
            fontSize: 12,
          }}
        >
          {r}
        </span>
      ),
    },
    {
      title: "Supplier",
      dataIndex: "supplier",
      key: "supplier",
      width: 180,
      render: (s: string) => <span style={{ fontWeight: 500 }}>{s || "—"}</span>,
    },
    { title: "Bid", dataIndex: "bid_number", key: "bid_number", width: 220 },
    {
      title: "Score",
      dataIndex: "overall_score",
      key: "overall_score",
      width: 100,
      render: (s: string) => <strong style={{ color: "#1e3a8a" }}>{formatPct(s)}</strong>,
    },
    {
      title: "Above Cutoff",
      dataIndex: "above_cutoff",
      key: "above_cutoff",
      width: 120,
      align: "center" as const,
      render: (v: boolean) =>
        v ? (
          <CheckCircleFilled style={{ color: "#16a34a", fontSize: 18 }} />
        ) : (
          <span style={{ color: "#bbb" }}>—</span>
        ),
    },
    { title: "Gross Value", dataIndex: "gross_value", key: "gross_value", width: 130, render: (v: string) => formatCr(v) },
    { title: "Net Value", dataIndex: "net_value", key: "net_value", width: 130, render: (v: string) => formatCr(v) },
    {
      title: "Compl%",
      dataIndex: "response_completeness_pct",
      key: "response_completeness_pct",
      width: 90,
      render: (v: string) => `${parseFloat(v).toFixed(0)}%`,
    },
    {
      title: "Submitted",
      dataIndex: "bid_submitted_at",
      key: "bid_submitted_at",
      width: 170,
      render: (v: string) => dayjs(v).format("DD/MM/YYYY HH:mm"),
    },
    {
      title: "Status",
      dataIndex: "eval_status",
      key: "eval_status",
      width: 110,
      render: (s: string) => <Tag color={s === "Approved" ? "green" : "default"}>{s}</Tag>,
    },
  ];

  // ---------------------- Compliance Q&A ----------------------
  const complianceColumns = [
    { title: "Question", dataIndex: "question_text", key: "question_text", ellipsis: true },
    {
      title: "Avg",
      dataIndex: "avg_score",
      key: "avg_score",
      width: 110,
      render: (v: string) => {
        const n = parseFloat(v);
        const color = n >= 80 ? "#16a34a" : n >= 60 ? "#f59e0b" : "#ef4444";
        return (
          <div style={{ minWidth: 90 }}>
            <Progress percent={n} size="small" strokeColor={color} showInfo={false} />
            <div style={{ fontSize: 11, color: "#555", marginTop: 2 }}>{n.toFixed(0)}%</div>
          </div>
        );
      },
    },
    {
      title: "Min",
      dataIndex: "min_score",
      key: "min_score",
      width: 70,
      align: "center" as const,
      render: (v: string) => `${parseFloat(v).toFixed(0)}`,
    },
    {
      title: "Max",
      dataIndex: "max_score",
      key: "max_score",
      width: 70,
      align: "center" as const,
      render: (v: string) => `${parseFloat(v).toFixed(0)}`,
    },
    {
      title: "Weight",
      dataIndex: "weightage",
      key: "weightage",
      width: 80,
      align: "center" as const,
      render: (v: string) => parseFloat(v).toFixed(0),
    },
    {
      title: "Pass Rate",
      dataIndex: "pass_rate_pct",
      key: "pass_rate_pct",
      width: 100,
      align: "center" as const,
      render: (v: string) => <Tag color="green">{`${parseFloat(v).toFixed(0)}%`}</Tag>,
    },
    { title: "Bids", dataIndex: "n_evaluated_bids", key: "n_evaluated_bids", width: 70, align: "center" as const },
  ];

  // ---------------------- Negotiation Targets fallback (highest_lowest_price_spread) ----------------------
  const negotiationColumns = [
    {
      title: "Material",
      dataIndex: "material_description",
      key: "material_description",
      width: 320,
      render: (v: string) => (
        <Tooltip title={v}>
          <span style={{ fontSize: 12 }}>{v}</span>
        </Tooltip>
      ),
    },
    {
      title: "Avg Qty",
      dataIndex: "avg_qty",
      key: "avg_qty",
      width: 110,
      align: "right" as const,
      render: (v: number) => formatNum(v, 0),
    },
    { title: "Avg ₹", dataIndex: "avg_unit_price", key: "avg_unit_price", width: 100, align: "right" as const, render: (v: number) => formatNum(v) },
    {
      title: "Lowest ₹",
      dataIndex: "lowest_unit_price",
      key: "lowest_unit_price",
      width: 100,
      align: "right" as const,
      render: (v: number) => <strong style={{ color: "#16a34a" }}>{formatNum(v)}</strong>,
    },
    {
      title: "Savings %",
      dataIndex: "savings_pct",
      key: "savings_pct",
      width: 110,
      align: "center" as const,
      render: (v: number) => (
        <Tag color="green" style={{ fontWeight: 600 }}>
          <ArrowUpOutlined /> {v.toFixed(1)}%
        </Tag>
      ),
    },
    {
      title: "Savings Potential",
      dataIndex: "savings_potential",
      key: "savings_potential",
      width: 150,
      align: "right" as const,
      render: (v: number) => <strong style={{ color: "#1e3a8a" }}>{formatCr(v)}</strong>,
    },
  ];

  // ---------------------- Compliance Scorecard ----------------------
  const renderComplianceScorecard = () => {
    const sm = scorecard.score_matrix || {};
    const sections: string[] = sm.sections || [];
    const sectionMeta: { title: string; weightage: number }[] = sm.section_meta || [];
    const rows: any[] = sm.rows || [];
    const findings: any[] = scorecard.ai_findings || [];
    const insights: any[] = scorecard.bid_insights || [];
    const summary = scorecard.summary || {};

    return (
      <div>
        {/* Summary strip */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 10,
            marginBottom: 14,
          }}
        >
          <div style={statBox("#eff6ff", "#bfdbfe")}>
            <div style={statLabel}>Suppliers</div>
            <div style={statValue}>{summary.n_suppliers ?? "—"}</div>
          </div>
          <div style={statBox("#eef2ff", "#c7d2fe")}>
            <div style={statLabel}>Sections</div>
            <div style={statValue}>{summary.n_sections ?? "—"}</div>
          </div>
          <div style={statBox("#fef3c7", "#fde68a")}>
            <div style={statLabel}>Total Questions</div>
            <div style={statValue}>{summary.n_questions_total ?? "—"}</div>
          </div>
          <div style={statBox("#f0fdf4", "#bbf7d0")}>
            <div style={statLabel}>Top Ranked</div>
            <div style={{ ...statValue, fontSize: 14 }}>
              {summary.top_ranked?.supplier || "—"}
            </div>
            <div style={{ fontSize: 11, color: "#16a34a", marginTop: 2 }}>
              Score {summary.top_ranked?.score ?? "—"}
            </div>
          </div>
        </div>

        {/* Score matrix */}
        {rows.length > 0 && (
          <div style={{ marginBottom: 18 }}>
            <h4 style={sectionHeading}>Section Score Matrix</h4>
            <div style={{ overflowX: "auto", border: "1px solid #eef0f3", borderRadius: 8 }}>
              <table style={{ borderCollapse: "separate", borderSpacing: 0, width: "100%", fontSize: 12 }}>
                <thead>
                  <tr style={{ background: "#f8fafc" }}>
                    <th style={matrixTh()}>Supplier</th>
                    <th style={matrixTh(true)}>Overall</th>
                    {sections.map((s, i) => {
                      const w = sectionMeta[i]?.weightage;
                      return (
                        <th key={s} style={matrixTh()}>
                          {s}
                          {w != null && (
                            <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 400 }}>
                              w: {w}
                            </div>
                          )}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.bid_number}>
                      <td style={matrixTd()}>
                        <div style={{ fontWeight: 600, color: "#0f172a" }}>{r.supplier}</div>
                        <div style={{ fontSize: 11, color: "#64748b" }}>
                          #{r.rank} · {r.bid_number}
                        </div>
                      </td>
                      <td style={{ ...matrixTd(true), fontWeight: 700, color: "#1e3a8a" }}>
                        {r.overall_score?.toFixed(1)}
                      </td>
                      {sections.map((s) => {
                        const cell = r.sections?.[s] || {};
                        const score = cell.score;
                        const best = cell.best_in_section;
                        const color =
                          score >= 80 ? "#16a34a" : score >= 60 ? "#d97706" : "#dc2626";
                        return (
                          <td key={s} style={matrixTd()}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <span style={{ fontWeight: 600, color }}>
                                {score != null ? score.toFixed(0) : "—"}
                              </span>
                              {best && (
                                <Tag
                                  color="gold"
                                  style={{
                                    margin: 0,
                                    padding: "0 6px",
                                    fontSize: 10,
                                    lineHeight: "16px",
                                    height: 16,
                                  }}
                                >
                                  best
                                </Tag>
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* AI findings */}
        {findings.length > 0 && (
          <div style={{ marginBottom: 18 }}>
            <h4 style={sectionHeading}>AI Findings</h4>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {findings.map((f, i) => {
                const severityColor =
                  f.severity === "warning"
                    ? { bg: "#fffbeb", border: "#fde68a", icon: "#d97706" }
                    : f.severity === "critical"
                    ? { bg: "#fef2f2", border: "#fecaca", icon: "#dc2626" }
                    : { bg: "#eff6ff", border: "#bfdbfe", icon: "#2563eb" };
                return (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      gap: 10,
                      background: severityColor.bg,
                      border: `1px solid ${severityColor.border}`,
                      borderRadius: 8,
                      padding: "10px 12px",
                    }}
                  >
                    <WarningFilled style={{ color: severityColor.icon, fontSize: 18, marginTop: 2 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                        <Tag style={{ margin: 0, textTransform: "capitalize" }}>{f.category?.replace(/_/g, " ")}</Tag>
                        {f.supplier && (
                          <span style={{ fontSize: 12, fontWeight: 600 }}>{f.supplier}</span>
                        )}
                        {f.section && (
                          <Tag color="blue" style={{ margin: 0 }}>{f.section}</Tag>
                        )}
                      </div>
                      <div style={{ fontSize: 12.5, color: "#334155", lineHeight: 1.5 }}>
                        {f.message}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Bid insights */}
        {insights.length > 0 && (
          <div>
            <h4 style={sectionHeading}>Per-Supplier Insight</h4>
            <Collapse
              accordion
              defaultActiveKey={[`bi-0`]}
              items={insights.map((b: any, i: number) => ({
                key: `bi-${i}`,
                label: (
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        background: b.rank === 1 ? "#fef3c7" : "#f3f4f6",
                        color: b.rank === 1 ? "#a16207" : "#6b7280",
                        fontWeight: 700,
                        fontSize: 11,
                      }}
                    >
                      {b.rank}
                    </span>
                    <strong style={{ color: "#0f172a" }}>{b.supplier}</strong>
                    <span style={{ color: "#64748b", fontSize: 12 }}>
                      {b.bid_number} · {b.overall_score?.toFixed(1)}/100
                    </span>
                    {b.verdict && (
                      <Tag color={verdictColor(b.verdict)} style={{ marginLeft: "auto" }}>
                        {b.verdict}
                      </Tag>
                    )}
                  </div>
                ),
                children: (
                  <div>
                    {b.summary && (
                      <p style={{ margin: 0, marginBottom: 10, color: "#334155", fontSize: 13, lineHeight: 1.55 }}>
                        {b.summary}
                      </p>
                    )}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                      <div>
                        <div style={subHeading("#16a34a")}>Strengths</div>
                        <ul style={bulletList}>
                          {(b.strengths || []).map((s: string, si: number) => (
                            <li key={si}>{s}</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <div style={subHeading("#dc2626")}>Weaknesses</div>
                        <ul style={bulletList}>
                          {(b.weaknesses || []).map((s: string, si: number) => (
                            <li key={si}>{s}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    {b.key_questions?.length > 0 && (
                      <div style={{ marginTop: 10 }}>
                        <div style={subHeading("#2563eb")}>Key Question Notes</div>
                        <ul style={bulletList}>
                          {b.key_questions.map((s: string, si: number) => (
                            <li key={si}>{s}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ),
              }))}
            />
          </div>
        )}
      </div>
    );
  };

  // ---------------------- Section Audit Drill-down ----------------------
  // Groups the flat `section_audits` array by supplier, then renders each
  // supplier as an outer Collapse panel that contains a row per section with
  // the section score, the evaluators table, and an inner Collapse listing
  // every question's flags / AI audit verdict / reasoning.
  const renderSectionAuditDrillDown = () => {
    const audits: any[] = scorecard.section_audits || [];
    if (!audits.length) return null;

    const groups = new Map<string, any[]>();
    for (const a of audits) {
      const key = a.supplier || a.bid_number || "Unknown";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(a);
    }

    const supplierGroups = Array.from(groups.entries()).map(([supplier, sections]) => {
      // Overall = section_score weighted by section_weightage. Weights add up
      // to ~100 in the sample payload, so this falls out as a percentage.
      const totalWeight = sections.reduce(
        (s, x) => s + (Number(x.section_weightage) || 0),
        0
      );
      const overallScore =
        totalWeight > 0
          ? sections.reduce(
              (s, x) =>
                s + (Number(x.section_score) || 0) * (Number(x.section_weightage) || 0),
              0
            ) / totalWeight
          : 0;
      const bidNumber = sections[0]?.bid_number;
      return { supplier, sections, overallScore, bidNumber };
    });

    const firstKey = supplierGroups[0]?.supplier;
    const fmtFlag = (f: string) =>
      f
        .replace(/_/g, " ")
        .replace(/^./, (c) => c.toUpperCase());
    const fmtVerdict = (v?: string) =>
      v
        ? v.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
        : "—";

    return (
      <div style={{ marginTop: 22 }}>
        <h3
          style={{
            fontSize: 18,
            fontWeight: 700,
            color: "#0f172a",
            margin: "0 0 12px",
          }}
        >
          Section Audit Drill-down
        </h3>
        <Collapse
          accordion={false}
          defaultActiveKey={firstKey ? [firstKey] : []}
          items={supplierGroups.map((g) => ({
            key: g.supplier,
            label: (
              <span style={{ fontSize: 14, color: "#0f172a" }}>
                <strong>{g.supplier}</strong>
                <span style={{ color: "#64748b", marginLeft: 8 }}>
                  — overall {g.overallScore.toFixed(1)}/100
                </span>
                {g.bidNumber && (
                  <span style={{ color: "#94a3b8", marginLeft: 8, fontSize: 12 }}>
                    {g.bidNumber}
                  </span>
                )}
              </span>
            ),
            children: (
              <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                {g.sections.map((s: any, sidx: number) => (
                  <div key={`${s.section}-${sidx}`}>
                    {/* Section header — coloured pill + score chip */}
                    <div
                      style={{
                        marginBottom: 8,
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        flexWrap: "wrap",
                      }}
                    >
                      <span
                        style={{
                          background: "#2563eb",
                          color: "#fff",
                          padding: "3px 10px",
                          borderRadius: 4,
                          fontWeight: 700,
                          fontSize: 13,
                        }}
                      >
                        {s.section} — section score:
                      </span>
                      <span
                        style={{
                          background: "#dbeafe",
                          color: "#1e3a8a",
                          padding: "3px 10px",
                          borderRadius: 4,
                          fontFamily:
                            'ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace',
                          fontSize: 12.5,
                          fontWeight: 600,
                        }}
                      >
                        {Number(s.section_score ?? 0).toFixed(1)}/100
                      </span>
                      {s.section_weightage != null && (
                        <span style={{ fontSize: 12, color: "#64748b" }}>
                          weightage {s.section_weightage}%
                        </span>
                      )}
                    </div>

                    {/* Evaluators table */}
                    {Array.isArray(s.evaluators) && s.evaluators.length > 0 && (
                      <Table
                        size="small"
                        bordered
                        pagination={false}
                        dataSource={s.evaluators}
                        rowKey={(_r, i) => `ev-${sidx}-${i ?? 0}`}
                        style={{ marginBottom: 10 }}
                        columns={[
                          { title: "Evaluated By", dataIndex: "name", key: "name" },
                          {
                            title: "Score",
                            dataIndex: "score",
                            key: "score",
                            render: (v: number) =>
                              v != null ? `${v}/100` : "—",
                          },
                          {
                            title: "Date",
                            dataIndex: "date",
                            key: "date",
                            render: (v: string) =>
                              v ? dayjs(v).format("DD-MMM-YYYY") : "—",
                          },
                          {
                            title: "Remarks",
                            dataIndex: "remarks",
                            key: "remarks",
                            render: (v: string) => v || "—",
                          },
                        ]}
                      />
                    )}

                    {/* Questions accordion */}
                    {Array.isArray(s.questions) && s.questions.length > 0 && (
                      <Collapse
                        bordered
                        size="small"
                        items={s.questions.map((q: any, qi: number) => ({
                          key: `q-${sidx}-${qi}`,
                          label: (
                            <span style={{ fontSize: 13 }}>
                              <strong>Q.</strong> {q.question_text}
                              <span style={{ color: "#64748b" }}>
                                {" "}
                                · weight {q.weightage}% · avg {q.avg_score}/100
                              </span>
                            </span>
                          ),
                          children: (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 10,
                              }}
                            >
                              {/* Supplier answer */}
                              <div>
                                <span
                                  style={{
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: "#475569",
                                  }}
                                >
                                  Supplier answer:{" "}
                                </span>
                                <span
                                  style={{
                                    fontSize: 12.5,
                                    color: q.supplier_answer
                                      ? "#334155"
                                      : "#94a3b8",
                                    fontStyle: q.supplier_answer
                                      ? "normal"
                                      : "italic",
                                  }}
                                >
                                  {q.supplier_answer || "No answer provided"}
                                </span>
                              </div>

                              {/* Per-evaluator scores */}
                              {Array.isArray(q.evaluator_scores) &&
                                q.evaluator_scores.length > 0 && (
                                  <Table
                                    size="small"
                                    bordered
                                    pagination={false}
                                    dataSource={q.evaluator_scores}
                                    rowKey={(_r, i) => `evs-${qi}-${i ?? 0}`}
                                    columns={[
                                      {
                                        title: "Evaluator",
                                        dataIndex: "evaluator",
                                        key: "evaluator",
                                      },
                                      {
                                        title: "Score",
                                        dataIndex: "score",
                                        key: "score",
                                        render: (v: number) => `${v}/100`,
                                      },
                                      {
                                        title: "Date",
                                        dataIndex: "date",
                                        key: "date",
                                        render: (v: string) =>
                                          v
                                            ? dayjs(v).format(
                                                "DD-MMM-YYYY HH:mm"
                                              )
                                            : "—",
                                      },
                                      {
                                        title: "Remarks",
                                        dataIndex: "remarks",
                                        key: "remarks",
                                        render: (v: string) => v || "—",
                                      },
                                    ]}
                                  />
                                )}

                              {/* Flags */}
                              {Array.isArray(q.flags) && q.flags.length > 0 && (
                                <div>
                                  <span
                                    style={{
                                      fontSize: 12,
                                      fontWeight: 600,
                                      color: "#92400e",
                                      marginRight: 6,
                                    }}
                                  >
                                    Flags:
                                  </span>
                                  {q.flags.map((f: string, fi: number) => (
                                    <Tag
                                      key={fi}
                                      color="orange"
                                      style={{ margin: "0 4px 4px 0" }}
                                    >
                                      {fmtFlag(f)}
                                    </Tag>
                                  ))}
                                </div>
                              )}

                              {/* AI Audit */}
                              {q.ai_audit && (
                                <div
                                  style={{
                                    background: "#eff6ff",
                                    border: "1px solid #bfdbfe",
                                    borderRadius: 8,
                                    padding: "10px 12px",
                                    fontSize: 12.5,
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 8,
                                      flexWrap: "wrap",
                                      marginBottom: 6,
                                    }}
                                  >
                                    <BulbFilled
                                      style={{ color: "#2563eb" }}
                                    />
                                    <strong style={{ color: "#1e3a8a" }}>
                                      AI Audit
                                    </strong>
                                    <Tag
                                      color={verdictColor(q.ai_audit.verdict)}
                                    >
                                      {fmtVerdict(q.ai_audit.verdict)}
                                    </Tag>
                                    {q.ai_audit.confidence && (
                                      <span
                                        style={{
                                          fontSize: 11,
                                          color: "#64748b",
                                        }}
                                      >
                                        Confidence:{" "}
                                        <strong>{q.ai_audit.confidence}</strong>
                                      </span>
                                    )}
                                  </div>
                                  {q.ai_audit.reasoning && (
                                    <div style={{ color: "#334155", lineHeight: 1.55 }}>
                                      {q.ai_audit.reasoning}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          ),
                        }))}
                      />
                    )}
                  </div>
                ))}
              </div>
            ),
          }))}
        />
      </div>
    );
  };

  // ---------------------- Line Pricing (Tender Insights) ----------------------
  const renderLinePricing = () => {
    const summary = pricing.summary || {};
    const overview = summary.price_overview || {};
    const ranking = summary.ranking || {};
    const competitiveness: any[] = summary.competitiveness || [];
    const lines: any[] = pricing.lines || [];
    const nSuppliers =
      competitiveness.length ||
      data.stats?.n_bids ||
      (data.bids_ranked?.length ?? 0);
    const nLines = lines.length || data.stats?.n_lines || 0;

    return (
      <div>
        {/* Tender Insights header */}
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 10,
            marginBottom: 12,
            flexWrap: "wrap",
          }}
        >
          <h3
            style={{
              fontSize: 18,
              fontWeight: 700,
              color: "#0f172a",
              margin: 0,
            }}
          >
            Tender Insights
          </h3>
          <span style={{ fontSize: 13, color: "#64748b" }}>
            {nSuppliers} suppliers · {nLines} line items
          </span>
        </div>

        {/* Price overview */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 10,
            marginBottom: 14,
          }}
        >
          <div style={statBox("#f0fdf4", "#bbf7d0")}>
            <div style={statLabel}>Lowest Grand Total</div>
            <div style={statValue}>{formatCr(overview.lowest_grand_total?.value)}</div>
            <div style={{ fontSize: 11, color: "#16a34a", marginTop: 2 }}>
              {overview.lowest_grand_total?.supplier}
            </div>
          </div>
          <div style={statBox("#fef2f2", "#fecaca")}>
            <div style={statLabel}>Highest Grand Total</div>
            <div style={statValue}>{formatCr(overview.highest_grand_total?.value)}</div>
            <div style={{ fontSize: 11, color: "#dc2626", marginTop: 2 }}>
              {overview.highest_grand_total?.supplier}
            </div>
          </div>
          <div style={statBox("#eff6ff", "#bfdbfe")}>
            <div style={statLabel}>Average Total</div>
            <div style={statValue}>{formatCr(overview.avg_grand_total)}</div>
          </div>
          <div style={statBox("#fef3c7", "#fde68a")}>
            <div style={statLabel}>Price Spread</div>
            <div style={statValue}>
              {overview.price_spread?.pct?.toFixed(2)}%
            </div>
            <div style={{ fontSize: 11, color: "#a16207", marginTop: 2 }}>
              {formatCr(overview.price_spread?.abs)}
            </div>
          </div>
        </div>

        {/* Ranking Summary */}
        {(ranking.top_evaluated || ranking.lowest_priced || ranking.best_value) && (
          <div style={{ marginBottom: 14 }}>
            <h4 style={sectionHeading}>Ranking Summary</h4>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 10,
              }}
            >
              <div style={statBox("#fef3c7", "#fde68a")}>
                <div style={statLabel}>Top Evaluated</div>
                <div style={{ ...statValue, fontSize: 15 }}>
                  {ranking.top_evaluated?.supplier || "—"}
                </div>
                <div style={{ fontSize: 11, color: "#a16207", marginTop: 2 }}>
                  {ranking.top_evaluated?.score != null
                    ? `${ranking.top_evaluated.score}/100`
                    : "—"}
                </div>
              </div>
              <div style={statBox("#f0fdf4", "#bbf7d0")}>
                <div style={statLabel}>Lowest Priced</div>
                <div style={{ ...statValue, fontSize: 15 }}>
                  {ranking.lowest_priced?.supplier || "—"}
                </div>
                <div style={{ fontSize: 11, color: "#16a34a", marginTop: 2 }}>
                  {formatCr(ranking.lowest_priced?.value)}
                </div>
              </div>
              <div style={statBox("#eff6ff", "#bfdbfe")}>
                <div style={statLabel}>Best Value</div>
                <div style={{ ...statValue, fontSize: 15 }}>
                  {ranking.best_value?.supplier || "—"}
                </div>
                <div style={{ fontSize: 11, color: "#2563eb", marginTop: 2 }}>
                  {ranking.best_value?.formula || ranking.best_value?.value_score || "—"}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Decision signal */}
        {ranking.decision_signal && (
          <div
            style={{
              background: "#eff6ff",
              border: "1px solid #bfdbfe",
              borderRadius: 10,
              padding: "12px 14px",
              marginBottom: 14,
              display: "flex",
              gap: 10,
            }}
          >
            <BulbFilled style={{ color: "#2563eb", fontSize: 18, marginTop: 2 }} />
            <div style={{ flex: 1, fontSize: 13, color: "#334155", lineHeight: 1.5 }}>
              {ranking.decision_signal}
            </div>
          </div>
        )}

        {/* Competitiveness */}
        {competitiveness.length > 0 && (
          <div style={{ marginBottom: 18 }}>
            <h4 style={sectionHeading}>Competitiveness Across Lines</h4>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 10,
              }}
            >
              {competitiveness.map((c: any) => {
                const total = c.total_lines || 1;
                const lowPct = (c.lowest_count / total) * 100;
                const highPct = (c.highest_count / total) * 100;
                return (
                  <div
                    key={c.supplier}
                    style={{
                      background: "#fff",
                      border: "1px solid #eef0f3",
                      borderRadius: 10,
                      padding: "10px 12px",
                    }}
                  >
                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6, color: "#0f172a" }}>
                      {c.supplier}
                    </div>
                    <div style={{ fontSize: 11, color: "#16a34a", marginBottom: 2 }}>
                      Lowest on {c.lowest_count}/{c.total_lines} lines
                    </div>
                    <Progress percent={lowPct} size="small" strokeColor="#16a34a" showInfo={false} />
                    <div style={{ fontSize: 11, color: "#dc2626", marginTop: 6, marginBottom: 2 }}>
                      Highest on {c.highest_count}/{c.total_lines} lines
                    </div>
                    <Progress percent={highPct} size="small" strokeColor="#dc2626" showInfo={false} />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Item-wise Price Comparison */}
        {lines.length > 0 && (
          <div style={{ marginBottom: 18 }}>
            <h4 style={sectionHeading}>
              Item-wise Price Comparison ({lines.length} items)
            </h4>
            <Table
              dataSource={lines}
              rowKey={(_r, i) => `pa-line-${i}`}
              size="small"
              bordered
              pagination={{ pageSize: 10, size: "small" }}
              expandable={{
                expandedRowRender: (line: any) => (
                  <div style={{ background: "#f8fafc", padding: "8px 12px", borderRadius: 6 }}>
                    <Table
                      dataSource={line.bids || []}
                      rowKey={(b) => b.bid_number}
                      size="small"
                      pagination={false}
                      columns={[
                        { title: "Rank", dataIndex: "rank", width: 60, align: "center" as const },
                        {
                          title: "Supplier",
                          dataIndex: "supplier",
                          render: (s: string, b: any) => (
                            <span>
                              {s}{" "}
                              {b.tag === "Lowest" && (
                                <Tag color="green" style={{ marginLeft: 4 }}>
                                  Lowest
                                </Tag>
                              )}
                              {b.tag === "Highest" && (
                                <Tag color="red" style={{ marginLeft: 4 }}>
                                  Highest
                                </Tag>
                              )}
                            </span>
                          ),
                        },
                        {
                          title: "Unit ₹",
                          dataIndex: "unit_price",
                          align: "right" as const,
                          render: (v: number) => formatNum(v),
                        },
                        {
                          title: "Qty",
                          dataIndex: "supplied_quantity",
                          align: "right" as const,
                          render: (v: number) => formatNum(v, 0),
                        },
                        {
                          title: "Line Total",
                          dataIndex: "line_total",
                          align: "right" as const,
                          render: (v: number) => formatCr(v),
                        },
                        {
                          title: "Δ vs Lowest",
                          dataIndex: "delta_vs_lowest_pct",
                          align: "center" as const,
                          render: (v: number) => {
                            const color = v === 0 ? "#16a34a" : v < 25 ? "#d97706" : "#dc2626";
                            return <span style={{ color, fontWeight: 600 }}>{v.toFixed(1)}%</span>;
                          },
                        },
                        {
                          title: "Lead",
                          dataIndex: "delivery_lead_days",
                          align: "center" as const,
                          width: 70,
                          render: (v: string) => `${v}d`,
                        },
                      ]}
                    />
                    {line.ai_insight && (
                      <div
                        style={{
                          marginTop: 10,
                          padding: "8px 10px",
                          background: "#eff6ff",
                          border: "1px solid #bfdbfe",
                          borderRadius: 6,
                          fontSize: 12,
                          color: "#1e3a8a",
                          display: "flex",
                          gap: 8,
                          alignItems: "flex-start",
                        }}
                      >
                        <BulbFilled style={{ color: "#2563eb", marginTop: 2 }} />
                        <span>{line.ai_insight}</span>
                      </div>
                    )}
                  </div>
                ),
              }}
              columns={[
                {
                  title: "Material",
                  dataIndex: "material_description",
                  render: (v: string) => (
                    <Tooltip title={v}>
                      <span style={{ fontSize: 12 }}>{v}</span>
                    </Tooltip>
                  ),
                },
                {
                  title: "Asked Qty",
                  dataIndex: "asked_qty",
                  width: 100,
                  align: "right" as const,
                  render: (v: number) => formatNum(v, 2),
                },
                {
                  title: "Lowest",
                  width: 170,
                  render: (_: any, r: any) => (
                    <span>
                      <strong style={{ color: "#16a34a" }}>{formatNum(r.lowest?.unit_price)}</strong>
                      <div style={{ fontSize: 11, color: "#64748b" }}>{r.lowest?.supplier}</div>
                    </span>
                  ),
                },
                {
                  title: "Highest",
                  width: 170,
                  render: (_: any, r: any) => (
                    <span>
                      <strong style={{ color: "#dc2626" }}>{formatNum(r.highest?.unit_price)}</strong>
                      <div style={{ fontSize: 11, color: "#64748b" }}>{r.highest?.supplier}</div>
                    </span>
                  ),
                },
                {
                  title: "Spread",
                  dataIndex: "spread_pct",
                  width: 100,
                  align: "center" as const,
                  render: (v: number) => {
                    const color = v > 100 ? "#ef4444" : v > 50 ? "#f59e0b" : "#16a34a";
                    return (
                      <Tag style={{ color, borderColor: color, background: "transparent" }}>
                        {v.toFixed(1)}%
                      </Tag>
                    );
                  },
                },
                {
                  title: "Line Total",
                  dataIndex: "sum_line_total",
                  width: 130,
                  align: "right" as const,
                  render: (v: number) => formatCr(v),
                },
              ]}
              scroll={{ x: "max-content" }}
            />
          </div>
        )}
      </div>
    );
  };

  // ---------------------- Highest/Lowest Price Spread ----------------------
  const renderHighestLowestSpread = () => {
    const negSupplier: any[] = pricing.negotiation_per_supplier || [];
    // Compute savings % per supplier (the payload doesn't include it).
    const supplierStats = negSupplier.map((s: any) => {
      const quoted = Number(s.total_quoted) || 0;
      const savings = Number(s.total_savings) || 0;
      const pct = quoted > 0 ? (savings / quoted) * 100 : 0;
      return { ...s, _pct: pct };
    });
    const totalLines =
      headroom.n_lines_with_headroom ||
      headroom.targets?.length ||
      pricing.lines?.length ||
      0;
    const totalSavings =
      headroom.total_savings_potential ||
      (supplierStats.length
        ? Math.max(...supplierStats.map((n: any) => Number(n.total_savings) || 0))
        : 0);

    // Default selection — supplier with the largest savings potential
    const sortedBySavings = [...supplierStats].sort(
      (a: any, b: any) =>
        (Number(b.total_savings) || 0) - (Number(a.total_savings) || 0)
    );
    const activeSupplierName =
      (negotiationSupplier &&
        supplierStats.find((s: any) => s.supplier === negotiationSupplier)?.supplier) ||
      sortedBySavings[0]?.supplier ||
      null;
    const selected = supplierStats.find((s: any) => s.supplier === activeSupplierName);

    if (negSupplier.length === 0 && (!headroom.targets || headroom.targets.length === 0)) {
      return (
        <div style={{ padding: "30px 0" }}>
          <Empty description="No negotiation data available" />
        </div>
      );
    }

    return (
      <div>
        {/* Header */}
        <div style={{ marginBottom: 14 }}>
          <h3
            style={{
              fontSize: 18,
              fontWeight: 700,
              color: "#0f172a",
              margin: 0,
              marginBottom: 4,
            }}
          >
            Tender-wide ceiling savings: {formatCr(totalSavings)} across {totalLines} lines
          </h3>
          <p style={{ fontSize: 12.5, color: "#64748b", lineHeight: 1.55, margin: 0 }}>
            Pick a supplier to see how their quoted prices compare against the
            best-of-rest on each line. The headroom column tells you how much you
            can realistically push for in negotiation before you'd be better off
            switching to another bidder.
          </p>
        </div>

        {/* Supplier dropdown */}
        {negSupplier.length > 0 && (
          <div style={{ marginBottom: 14, maxWidth: 520 }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: "#1e3a8a",
                marginBottom: 6,
              }}
            >
              Negotiate with which supplier?
            </div>
            <Select
              value={activeSupplierName || undefined}
              onChange={(val: string) => setNegotiationSupplier(val)}
              style={{ width: "100%" }}
              optionLabelProp="label"
              options={sortedBySavings.map((s: any) => ({
                value: s.supplier,
                label: s.supplier,
                supplier: s.supplier,
                savings: Number(s.total_savings) || 0,
                pct: Number(s._pct) || 0,
              }))}
              optionRender={(opt) => {
                const raw: any = opt.data;
                return (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 10,
                      alignItems: "center",
                    }}
                  >
                    <span style={{ fontWeight: 600, color: "#0f172a" }}>
                      {raw.supplier}
                    </span>
                    <span style={{ fontSize: 12, color: "#16a34a", fontWeight: 600 }}>
                      {formatCr(raw.savings)} · {raw.pct.toFixed(1)}%
                    </span>
                  </div>
                );
              }}
            />
          </div>
        )}

        {/* 4 KPI cards for the selected supplier */}
        {selected && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 10,
              marginBottom: 16,
            }}
          >
            <div style={statBox("#eff6ff", "#bfdbfe")}>
              <div style={statLabel}>Their quoted total</div>
              <div style={statValue}>{formatCr(selected.total_quoted)}</div>
              <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                {selected.n_lines_bid} lines bid
              </div>
            </div>
            <div style={statBox("#f0fdf4", "#bbf7d0")}>
              <div style={statLabel}>Best-of-rest target</div>
              <div style={statValue}>{formatCr(selected.target_total)}</div>
              <div style={{ fontSize: 11, color: "#16a34a", marginTop: 2 }}>
                If every line dropped to lowest competitor
              </div>
            </div>
            <div style={statBox("#fef3c7", "#fde68a")}>
              <div style={statLabel}>Negotiation headroom</div>
              <div style={statValue}>{formatCr(selected.total_savings)}</div>
              <div style={{ fontSize: 11, color: "#a16207", marginTop: 2 }}>
                <ArrowUpOutlined />{" "}
                {Number(selected._pct || 0).toFixed(1)}% off
              </div>
            </div>
            <div style={statBox("#ede9fe", "#ddd6fe")}>
              <div style={statLabel}>Already lowest on</div>
              <div style={statValue}>
                {selected.n_already_lowest}/{selected.n_lines_bid}
              </div>
              <div style={{ fontSize: 11, color: "#7c3aed", marginTop: 2 }}>
                {selected.n_negotiable} negotiable
              </div>
            </div>
          </div>
        )}

        {/* Per-item leverage table */}
        {selected && Array.isArray(selected.lines) && selected.lines.length > 0 ? (
          <div>
            <h4 style={sectionHeading}>Per-item leverage — {selected.supplier}</h4>
            <Table
              dataSource={selected.lines}
              rowKey={(_r, i) => `lev-${i}`}
              size="small"
              bordered
              pagination={{ pageSize: 10, size: "small" }}
              scroll={{ x: "max-content" }}
              columns={[
                {
                  title: "Item",
                  dataIndex: "material_description",
                  key: "material_description",
                  width: 320,
                  render: (v: string) => (
                    <Tooltip title={v}>
                      <span style={{ fontSize: 12 }}>{v || "—"}</span>
                    </Tooltip>
                  ),
                },
                {
                  title: "Their price",
                  dataIndex: "their_price",
                  key: "their_price",
                  width: 110,
                  align: "right" as const,
                  render: (v: number) => formatNum(v),
                },
                {
                  title: "Best other",
                  dataIndex: "best_other_price",
                  key: "best_other_price",
                  width: 110,
                  align: "right" as const,
                  render: (v: number) => (
                    <strong style={{ color: "#16a34a" }}>{formatNum(v)}</strong>
                  ),
                },
                {
                  title: "Saving/unit",
                  dataIndex: "savings_per_unit",
                  key: "savings_per_unit",
                  width: 120,
                  align: "right" as const,
                  render: (v: number) => formatNum(v),
                },
                {
                  title: "Qty",
                  dataIndex: "qty",
                  key: "qty",
                  width: 90,
                  align: "right" as const,
                  render: (v: number) => formatNum(v, 0),
                },
                {
                  title: "Total saving",
                  dataIndex: "savings_total",
                  key: "savings_total",
                  width: 130,
                  align: "right" as const,
                  render: (v: number) => (
                    <strong style={{ color: "#1e3a8a" }}>{formatCr(v)}</strong>
                  ),
                },
                {
                  title: "Negotiate vs",
                  dataIndex: "best_other_supplier",
                  key: "best_other_supplier",
                  width: 180,
                  render: (v: string, r: any) =>
                    r.already_lowest ? (
                      <Tag color="green" style={{ margin: 0 }}>
                        Already lowest
                      </Tag>
                    ) : v ? (
                      <Tag color="blue" style={{ margin: 0 }}>
                        {v}
                      </Tag>
                    ) : (
                      <span style={{ color: "#94a3b8" }}>—</span>
                    ),
                },
              ]}
            />
          </div>
        ) : (
          // Fallback — when negotiation_per_supplier doesn't carry per-line detail
          // we still surface the tender-wide headroom targets.
          headroom.targets &&
          headroom.targets.length > 0 && (
            <div>
              <h4 style={sectionHeading}>Tender-wide negotiation targets</h4>
              <Table
                dataSource={headroom.targets || []}
                columns={negotiationColumns}
                pagination={{ pageSize: 10, size: "small" }}
                size="small"
                bordered
                rowKey={(_r, i) => `target-${i}`}
                scroll={{ x: "max-content" }}
              />
            </div>
          )
        )}
      </div>
    );
  };

  // ---------------------- Supplier History v2 ----------------------
  const renderSupplierHistoryV2 = () => {
    const suppliers: any[] = historyV2.suppliers || [];
    const dimKeys: string[] = historyV2.dimension_keys || [];
    const dimLabels: Record<string, string> = historyV2.dimension_labels || {};

    if (suppliers.length === 0) {
      // Fall back to the old supplier_history table if v2 is missing.
      return (
        <Table
          dataSource={data.supplier_history}
          columns={[
            { title: "Supplier", key: "supplier", render: (_: any, __: any, idx: number) => `Supplier ${idx + 1}` },
            { title: "Invited", dataIndex: "n_invited" },
            { title: "Responded", dataIndex: "n_responded" },
            { title: "Won", dataIndex: "n_won" },
            { title: "Win Rate", dataIndex: "win_rate_pct", render: (v: string) => `${parseFloat(v).toFixed(0)}%` },
            { title: "Response Rate", dataIndex: "response_rate_pct", render: (v: string) => `${parseFloat(v).toFixed(0)}%` },
            { title: "Total Quoted", dataIndex: "total_quoted_value", render: (v: string) => formatCr(v) },
          ]}
          pagination={false}
          size="small"
          bordered
          rowKey={(_r, i) => `sh-${i}`}
        />
      );
    }

    // Filter by supplier name / bid number first, then page-slice the
    // remainder. The first visible row is auto-expanded.
    const q = supplierSearch.trim().toLowerCase();
    const filtered = q
      ? suppliers.filter(
          (s) =>
            (s.supplier_name || "").toLowerCase().includes(q) ||
            (s.bid_number || "").toLowerCase().includes(q)
        )
      : suppliers;

    const total = filtered.length;
    const safePage = Math.min(
      supplierPage,
      Math.max(1, Math.ceil(total / SUPPLIER_PAGE_SIZE))
    );
    const start = (safePage - 1) * SUPPLIER_PAGE_SIZE;
    const pageItems = filtered.slice(start, start + SUPPLIER_PAGE_SIZE);
    const firstKey =
      pageItems[0]?.bid_number || (pageItems[0] ? `sh-${0}` : undefined);

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {/* Search + result count */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <Input
            placeholder="Search supplier name or bid number…"
            prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
            allowClear
            value={supplierSearch}
            onChange={(e) => {
              setSupplierSearch(e.target.value);
              setSupplierPage(1);
            }}
            style={{ maxWidth: 340 }}
          />
          <span style={{ fontSize: 12, color: "#64748b" }}>
            Showing {total === 0 ? 0 : start + 1}–
            {Math.min(start + SUPPLIER_PAGE_SIZE, total)} of {total}
            {q ? ` (filtered from ${suppliers.length})` : ""}
          </span>
        </div>

        {total === 0 ? (
          <Empty description="No suppliers match your search" />
        ) : (
          <Collapse
            accordion={false}
            defaultActiveKey={firstKey ? [firstKey] : []}
            // Key the Collapse on the page boundaries + filter, so when the
            // user paginates or searches, AntD re-mounts and the first item
            // of the new page becomes the open one again.
            key={`supplier-history-${safePage}-${q}`}
            items={pageItems.map((s, idx) => {
              const t = s.structured?.this_tender || {};
              const ph = s.structured?.past_history || {};
              const oa = s.overall_assessment || {};
              const key = s.bid_number || `sh-${idx}`;
              return {
                key,
                label: (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      flexWrap: "wrap",
                      width: "100%",
                    }}
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 26,
                        height: 26,
                        borderRadius: "50%",
                        background: t.rank === 1 ? "#fef3c7" : "#f3f4f6",
                        color: t.rank === 1 ? "#a16207" : "#6b7280",
                        fontWeight: 700,
                        fontSize: 12,
                      }}
                    >
                      {t.rank ?? "—"}
                    </span>
                    <strong style={{ fontSize: 14, color: "#0f172a" }}>
                      {s.supplier_name}
                    </strong>
                    <span style={{ color: "#64748b", fontSize: 12 }}>
                      {s.bid_number}
                    </span>
                    <span style={{ color: "#475569", fontSize: 12 }}>
                      Score <strong>{t.score?.toFixed(1) ?? "—"}</strong>
                    </span>
                    <span style={{ color: "#475569", fontSize: 12 }}>
                      Gross <strong>{formatCr(t.gross_value)}</strong>
                    </span>
                    <span style={{ color: "#475569", fontSize: 12 }}>
                      Win rate{" "}
                      <strong>
                        {ph.win_rate_pct?.toFixed?.(0) ?? ph.win_rate_pct ?? 0}%
                      </strong>
                    </span>
                    {oa.verdict && (
                      <Tag
                        color={verdictColor(oa.verdict)}
                        style={{ marginLeft: "auto" }}
                      >
                        {oa.verdict}
                      </Tag>
                    )}
                  </div>
                ),
                children: (
                  <div>
                    {/* Summary */}
                    {oa.summary && (
                      <p
                        style={{
                          fontSize: 13,
                          color: "#334155",
                          lineHeight: 1.55,
                          margin: "0 0 10px",
                        }}
                      >
                        {oa.summary}
                      </p>
                    )}

                    {/* Strengths / Concerns — card style with larger text */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 12,
                        marginBottom: 14,
                      }}
                    >
                      <div
                        style={{
                          background: "#f0fdf4",
                          border: "1px solid #bbf7d0",
                          borderRadius: 10,
                          padding: "12px 14px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            marginBottom: 8,
                          }}
                        >
                          <CheckCircleOutlined
                            style={{ color: "#16a34a", fontSize: 15 }}
                          />
                          <span
                            style={{
                              fontSize: 14,
                              fontWeight: 700,
                              color: "#14532d",
                            }}
                          >
                            Key Strengths
                          </span>
                        </div>
                        <ul
                          style={{
                            margin: 0,
                            paddingLeft: 20,
                            fontSize: 13.5,
                            color: "#1f3a2a",
                            lineHeight: 1.6,
                            listStyleType: "disc",
                          }}
                        >
                          {(oa.key_strengths || []).map(
                            (x: string, xi: number) => (
                              <li key={xi} style={{ marginBottom: 4 }}>{x}</li>
                            )
                          )}
                        </ul>
                      </div>
                      <div
                        style={{
                          background: "#fef2f2",
                          border: "1px solid #fecaca",
                          borderRadius: 10,
                          padding: "12px 14px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            marginBottom: 8,
                          }}
                        >
                          <CloseCircleFilled
                            style={{ color: "#dc2626", fontSize: 15 }}
                          />
                          <span
                            style={{
                              fontSize: 14,
                              fontWeight: 700,
                              color: "#7f1d1d",
                            }}
                          >
                            Key Concerns
                          </span>
                        </div>
                        <ul
                          style={{
                            margin: 0,
                            paddingLeft: 20,
                            fontSize: 13.5,
                            color: "#3a1f1f",
                            lineHeight: 1.6,
                            listStyleType: "disc",
                          }}
                        >
                          {(oa.key_concerns || []).map(
                            (x: string, xi: number) => (
                              <li key={xi} style={{ marginBottom: 4 }}>{x}</li>
                            )
                          )}
                        </ul>
                      </div>
                    </div>

                    {/* Dimension grid */}
                    <div>
                      <div style={subHeading("#1e3a8a")}>Dimension Assessment</div>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                          gap: 8,
                          marginTop: 6,
                        }}
                      >
                        {dimKeys.map((k) => {
                          const dim = s.dimensions?.[k];
                          if (!dim) return null;
                          const meta = ratingMeta(dim.rating);
                          return (
                            <div
                              key={k}
                              style={{
                                background: meta.bg,
                                border: `1px solid ${meta.border}`,
                                borderRadius: 8,
                                padding: "8px 10px",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  marginBottom: 4,
                                  gap: 6,
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: "#0f172a",
                                  }}
                                >
                                  {dimLabels[k] || k}
                                </span>
                                <span
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 4,
                                    fontSize: 11,
                                    color: meta.color,
                                    fontWeight: 600,
                                  }}
                                >
                                  {meta.icon}
                                  {meta.label}
                                </span>
                              </div>
                              <div
                                style={{
                                  fontSize: 12,
                                  color: "#475569",
                                  lineHeight: 1.45,
                                }}
                              >
                                {dim.summary}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ),
              };
            })}
          />
        )}

        {/* Pagination */}
        {total > SUPPLIER_PAGE_SIZE && (
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 4 }}>
            <Pagination
              current={safePage}
              pageSize={SUPPLIER_PAGE_SIZE}
              total={total}
              size="small"
              onChange={(p) => setSupplierPage(p)}
              showSizeChanger={false}
            />
          </div>
        )}
      </div>
    );
  };

  // ---------------------- Early returns ----------------------
  // Must come BEFORE the innerTabs / main JSX, because both reference data.*
  // directly (data.bids_ranked, data.compliance, data.line_pricing, etc.).
  if (loading) {
    const tenderLabel =
      tenderData?.tenderNumber ||
      (tenderRef != null ? String(tenderRef) : undefined);
    // While regenerating (force regen OR cold load with no cached history),
    // show the full-screen radar loader. The inline spinner falls through
    // only for the quick "warm load" path where we're pulling a cached
    // analysis from the agent.
    if (regenerating) {
      return <RegenerateLoader tenderTitle={tenderLabel} />;
    }
    return (
      <div
        style={{
          padding: "60px 16px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 14,
        }}
      >
        <Spin size="large" />
        <span style={{ color: "#475569", fontSize: 13 }}>
          Loading AI insights…
        </span>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ padding: "40px 16px" }}>
        <Empty
          description={
            <div style={{ color: "#64748b", maxWidth: 460, margin: "0 auto" }}>
              {error || "No AI analysis available for this tender yet."}
            </div>
          }
        >
          <Button type="primary" onClick={() => fetchAnalysis(false)} loading={loading}>
            Retry
          </Button>
        </Empty>
      </div>
    );
  }

  // ---------------------- Tabs ----------------------
  const innerTabs = [
    {
      key: "ranked",
      label: "Ranked Bids",
      children: (
        <>
          <Table
            dataSource={data.bids_ranked}
            columns={rankedColumns}
            pagination={false}
            size="small"
            bordered
            rowKey="bid_number"
            scroll={{ x: "max-content" }}
          />
          {data.cutoff?.rationale && (
            <div style={{ marginTop: 10, fontSize: 12, color: "#5a5a5a", fontStyle: "italic" }}>
              {data.cutoff.rationale}
            </div>
          )}
        </>
      ),
    },
    {
      key: "compliance",
      label: "Compliance",
      children: (
        <div>
          {renderComplianceScorecard()}
          {renderSectionAuditDrillDown()}
        </div>
      ),
    },
    { key: "line", label: "Line Pricing", children: renderLinePricing() },
    {
      key: "spread",
      label: "Highest/Lowest Price Spread",
      children: renderHighestLowestSpread(),
    },
    { key: "history", label: "Supplier History", children: renderSupplierHistoryV2() },
    {
      key: "markdown",
      label: "Full Report",
      children: (
        <div>
          {/* The tabpane CSS already gives us the card chrome — just lay out
              the top row (title left, full-screen button right) and let the
              tab content frame handle the single scrollbar. */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              marginBottom: 12,
            }}
          >
            <Tooltip title="Open the report over the whole window (Esc to close)">
              <Button
                type="default"
                icon={<FullscreenOutlined />}
                onClick={() => setReportFullscreen(true)}
                disabled={!data.content_md}
                style={{ borderRadius: 8 }}
              >
                View in Full Screen
              </Button>
            </Tooltip>
          </div>
          <div>
            {data.content_md ? (
                  <MarkdownMessage text={data.content_md} />
                ) : (
                  <Empty />
                )}
          </div>
        </div>
      ),
    },
  ];

  // ---------------------- Render ----------------------
  return (
    <DocViewerProvider>
    <div style={{ padding: "16px 0" }}>
      <style>{`
        /* Collapse the KPI/Recommendation 2-column dashboard on narrow viewports */
        @media (max-width: 980px) {
          .ai-dashboard-row {
            grid-template-columns: 1fr !important;
          }
        }
        .ai-rec-points li::marker { color: #f59e0b; }
        /* Two-phase scroll for the inner tabs:
             Phase 1 — page scroll: the dashboard scrolls up while the whole
                       tabs card rides up with it.
             Phase 2 — sticky lock: once the card top reaches the top of
                       the viewport, the sticky position anchors it there.
                       The card occupies a bounded viewport height; the
                       strip stays at the top and only the tab body scrolls.
           Tweak top: 0 to your fixed-nav height if the app has one
           (e.g. top: 64px). */
        .ai-insight-tabs {
          position: sticky;
          top: 0;
          height: calc(100vh - 200px);
          min-height: 340px;
          display: flex;
          flex-direction: column;
          background: #fff;
          border: 1px solid #e6e8eb;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
          z-index: 5;
        }
        .ai-insight-tabs > .ant-tabs-nav {
          flex-shrink: 0;
          background: linear-gradient(180deg, #fafbfc 0%, #f5f7fa 100%);
          padding: 0 18px;
          margin: 0 !important;
          border-bottom: 1px solid #eef0f3;
        }
        .ai-insight-tabs > .ant-tabs-nav::before {
          border-bottom: none !important;
        }
        .ai-insight-tabs .ant-tabs-tab {
          padding: 12px 4px !important;
          font-weight: 500;
        }
        .ai-insight-tabs .ant-tabs-tab + .ant-tabs-tab {
          margin-left: 28px !important;
        }
        .ai-insight-tabs .ant-tabs-tab-active .ant-tabs-tab-btn {
          color: #2263b3 !important;
          font-weight: 600;
        }
        .ai-insight-tabs .ant-tabs-ink-bar {
          background: #2263b3 !important;
          height: 3px !important;
          border-radius: 2px 2px 0 0;
        }
        /* Tab body — fills the remaining height of the (now-sticky) card and
           scrolls internally once content overflows. */
        .ai-insight-tabs > .ant-tabs-content-holder {
          flex: 1;
          min-height: 0;
          overflow-y: auto;
          padding: 18px 22px;
          background: #fff;
        }
        .ai-insight-tabs .ant-tabs-tabpane {
          background: transparent;
          border: none;
          border-radius: 0;
          padding: 0;
        }
        .ai-insight-tabs > .ant-tabs-content-holder::-webkit-scrollbar {
          width: 8px;
        }
        .ai-insight-tabs > .ant-tabs-content-holder::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 4px;
        }
        .ai-insight-tabs > .ant-tabs-content-holder::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
        @keyframes ai-regen-spark {
          0%, 100% { transform: scale(1) rotate(0deg); filter: drop-shadow(0 0 0 rgba(250, 204, 21, 0)); }
          50% { transform: scale(1.18) rotate(-8deg); filter: drop-shadow(0 0 6px rgba(250, 204, 21, 0.95)); }
        }
        .ai-regen-spark {
          animation: ai-regen-spark 1.6s ease-in-out infinite;
          transform-origin: center;
        }
        @keyframes ai-regen-glow {
          0%, 100% { box-shadow: 0 4px 14px rgba(37, 99, 235, 0.35); }
          50% { box-shadow: 0 6px 22px rgba(96, 165, 250, 0.6); }
        }
        .ant-btn.ai-regen-btn:not(:disabled):not(.ant-btn-loading) {
          animation: ai-regen-glow 2.4s ease-in-out infinite;
        }
        .ant-btn.ai-regen-btn:not(:disabled):hover {
          transform: translateY(-1px);
          filter: brightness(1.08);
        }
      `}</style>
      {/* Header */}
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: "#0f172a", marginBottom: 6 }}>
            {data.header?.tender_id || data.tender_id}
            {data.header?.tender_title ? ` — ${data.header.tender_title}` : ""}
          </h2>
          <div
            style={{
              display: "flex",
              gap: 16,
              flexWrap: "wrap",
              color: "#5a5a5a",
              fontSize: 13,
            }}
          >
            {data.header?.tender_number && (
              <span>{data.header.tender_number}</span>
            )}
            {data.header?.tender_type && (
              <>
                <span>·</span>
                <span>
                  {data.header.tender_type}
                  {data.header.tender_for ? ` (${data.header.tender_for})` : ""}
                </span>
              </>
            )}
            {data.header?.status && (
              <>
                <span>·</span>
                <span>
                  Status:{" "}
                  <strong style={{ color: "#16a34a" }}>
                    {data.header.status}
                  </strong>
                </span>
              </>
            )}
            {data.header?.award_status && (
              <>
                <span>·</span>
                <span>Award: {data.header.award_status}</span>
              </>
            )}
            {data.header?.approval_status && (
              <>
                <span>·</span>
                <span>Approval: {data.header.approval_status}</span>
              </>
            )}
            {data.version != null && (
              <>
                <span>·</span>
                <span>
                  Version <strong>v{data.version}</strong>
                  {/* {data.from_cache ? " (cached)" : ""} */}
                </span>
              </>
            )}
          </div>
          {data.header?.description && (
            <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>
              {data.header.description}
            </div>
          )}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            flexDirection: "column",
            gap: 4,
          }}
        >
          <Tooltip title="Force a fresh LLM run (~30 s, ~$0.05). Use only when bids or compliance scores changed.">
            <span
              style={{
                position: "relative",
                display: "inline-block",
                borderRadius: 8,
                overflow: "hidden",
                isolation: "isolate",
              }}
            >
              <Button
                className="ai-regen-btn"
                icon={
                  <ThunderboltFilled
                    className="ai-regen-spark"
                    style={{ color: "#facc15", fontSize: 16 }}
                  />
                }
                onClick={handleRegenerate}
                loading={loading && regenerating}
                disabled={loading}
                style={{
                  background:
                    "linear-gradient(135deg, #1e3a8a 0%, #2563eb 50%, #60a5fa 100%)",
                  border: "none",
                  color: "#fff",
                  fontWeight: 600,
                  borderRadius: 8,
                  position: "relative",
                  zIndex: 1,
                }}
              >
                Regenerate
              </Button>
              {/* Glossy shine — framer-motion slides a translucent streak
                  across the button on a loop. Skip while disabled/loading
                  to avoid visual noise during the actual LLM call. */}
              {!(loading || regenerating) && (
                <motion.span
                  aria-hidden
                  style={{
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    width: "55%",
                    pointerEvents: "none",
                    background:
                      "linear-gradient(105deg, transparent 0%, transparent 30%, rgba(255,255,255,0.55) 50%, transparent 70%, transparent 100%)",
                    filter: "blur(2px)",
                    zIndex: 2,
                  }}
                  initial={{ x: "-120%" }}
                  animate={{ x: "220%" }}
                  transition={{
                    duration: 1.8,
                    ease: "easeInOut",
                    repeat: Infinity,
                    repeatDelay: 1.6,
                  }}
                />
              )}
            </span>
          </Tooltip>
          {freshness?.last_sync?.completed_at_ist && (
            <Tooltip
              title={
                freshness.schedule
                  ? `Sync schedule: ${freshness.schedule}${
                      freshness.last_sync.human
                        ? ` — ${freshness.last_sync.human}`
                        : ""
                    }`
                  : undefined
              }
            >
              <div
                style={{
                  fontSize: 11,
                  color: "#64748b",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                }}
              >
                Last updated at:{" "}
                <span style={{ color: "#334155" }}>
                  {freshness.last_sync.completed_at_ist}
                </span>
              </div>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Two-column dashboard: KPI rail (left) + Recommended Bid hero (right) */}
      <div
        className="ai-dashboard-row"
        style={{
          display: "grid",
          gridTemplateColumns: data.recommendation ? "1fr 1fr" : "1fr",
          gap: 16,
          marginBottom: 16,
          alignItems: "stretch",
        }}
      >
        {/* Left: KPI list — summary-style with alternating light rows */}
        <div
          style={{
            background: "#fff",
            border: "1px solid #eef0f3",
            borderRadius: 0,
            padding: "18px 20px 16px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 10,
              paddingBottom: 12,
              borderBottom: "1px solid #eef0f3",
              marginBottom: 6,
            }}
          >
            <div>
              <h3
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: "#1e3a8a",
                  margin: 0,
                  marginBottom: 2,
                }}
              >
                Key Metrics
              </h3>
              <div
                style={{
                  fontSize: 12,
                  color: "#94a3b8",
                  fontWeight: 500,
                }}
              >
                Snapshot of bid evaluation statistics
              </div>
            </div>
            <Tooltip title="Regenerate AI analysis">
              <Button
                type="text"
                shape="circle"
                icon={<RiseOutlined rotate={-90} style={{ fontSize: 15, color: "#475569" }} />}
                onClick={handleRegenerate}
                loading={loading && regenerating}
                disabled={loading}
                style={{ flexShrink: 0 }}
              />
            </Tooltip>
          </div>

          {/* Rows — full-width alternating strips, no per-row radius.
              Side margins are negative so the strips reach the card edges.
              flex:1 lets the strip group stretch to fill any extra card
              height (the right Recommended-Bid card usually drives the row
              height), and each individual strip then flex:1s evenly so the
              rows distribute the leftover space instead of leaving white
              gap below the last item. */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              marginLeft: -20,
              marginRight: -20,
              overflow: "hidden",
            }}
          >
            {kpiCards.map((k, idx) => (
              <div
                key={k.label}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "12px 20px",
                  background: idx % 2 === 0 ? "#f5f7fa" : "#ffffff",
                  flex: 1,
                  minHeight: 56,
                }}
              >
                <span
                  style={{
                    color: "#475569",
                    fontSize: 17,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {k.icon}
                </span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      flexWrap: "wrap",
                    }}
                  >
                    <span
                      style={{
                        fontSize: 14,
                        color: "#0f172a",
                        fontWeight: 700,
                        lineHeight: 1.25,
                      }}
                    >
                      {k.label}
                    </span>
                    {k.tag && (
                      <Tag
                        color="green"
                        style={{
                          margin: 0,
                          background: "#f0fdf4",
                          border: "1px solid #bbf7d0",
                          color: k.tag.color,
                          fontWeight: 500,
                          borderRadius: 10,
                          fontSize: 10.5,
                        }}
                      >
                        <ArrowUpOutlined /> {k.tag.text}
                      </Tag>
                    )}
                  </div>
                  {k.subValue && (
                    <div
                      style={{
                        fontSize: 11.5,
                        color: "#64748b",
                        marginTop: 2,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        fontWeight: 500,
                      }}
                      title={k.subValue}
                    >
                      {k.subValue}
                    </div>
                  )}
                </div>
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 500,
                    color: "#2563b3",
                    lineHeight: 1.1,
                    flexShrink: 0,
                  }}
                >
                  {k.value}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Recommended Bid hero */}
        {data.recommendation && (
          <div
            style={{
              position: "relative",
              background:
                "linear-gradient(135deg, #fef3c7 0%, #fffbeb 50%, #fef3c7 100%)",
              border: "1px solid #fde68a",
              borderRadius: 0,
              padding: "18px 20px",
              boxShadow: "0 4px 16px rgba(161, 98, 7, 0.12)",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Decorative trophy halo */}
            <div
              style={{
                position: "absolute",
                top: -30,
                right: -30,
                width: 140,
                height: 140,
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, rgba(250, 204, 21, 0.25) 0%, transparent 70%)",
                pointerEvents: "none",
              }}
            />
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 12,
                position: "relative",
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 18,
                  boxShadow: "0 4px 12px rgba(245, 158, 11, 0.35)",
                }}
              >
                <TrophyOutlined />
              </div>
              <div>
                <div
                  style={{
                    fontSize: 11,
                    color: "#a16207",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  AI Recommendation
                </div>
                <h3
                  style={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: "#1a1a1a",
                    margin: 0,
                    lineHeight: 1.2,
                  }}
                >
                  Recommended Bid
                </h3>
              </div>
            </div>

            <div
              style={{
                fontSize: 18,
                fontWeight: 500,
                color: "#0f172a",
                marginBottom: 2,
              }}
            >
              {data.recommendation.supplier || data.recommendation.bid_number}
            </div>
            <div style={{ fontSize: 12, color: "#78350f", marginBottom: 10 }}>
              {data.recommendation.bid_number}
              {data.recommendation.tender_number
                ? ` · ${data.recommendation.tender_number}`
                : ""}
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 10,
                marginBottom: 12,
              }}
            >
              <div
                style={{
                  background: "rgba(255, 255, 255, 0.7)",
                  backdropFilter: "blur(4px)",
                  border: "1px solid rgba(253, 230, 138, 0.8)",
                  borderRadius: 10,
                  padding: "10px 12px",
                }}
              >
                <div style={{ fontSize: 10.5, color: "#92400e", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.3 }}>
                  Score
                </div>
                <div
                  style={{
                    fontSize: 22,
                    fontWeight: 500,
                    color: "#0f172a",
                    marginTop: 2,
                  }}
                >
                  {formatPct(data.recommendation.overall_score)}
                </div>
                {data.recommendation.eval_status && (
                  <Tag
                    color="green"
                    style={{ marginTop: 4, fontSize: 10.5 }}
                  >
                    {data.recommendation.eval_status}
                  </Tag>
                )}
              </div>
              <div
                style={{
                  background: "rgba(255, 255, 255, 0.7)",
                  backdropFilter: "blur(4px)",
                  border: "1px solid rgba(253, 230, 138, 0.8)",
                  borderRadius: 10,
                  padding: "10px 12px",
                }}
              >
                <div style={{ fontSize: 10.5, color: "#92400e", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.3 }}>
                  Bid Value
                </div>
                <div
                  style={{
                    fontSize: 22,
                    fontWeight: 500,
                    color: "#0f172a",
                    marginTop: 2,
                  }}
                >
                  {formatCrJSX(data.recommendation.gross_value)}
                </div>
                <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>
                  Compl {formatPct(data.recommendation.response_completeness_pct)}
                </div>
              </div>
            </div>

            {data.recommendation.rationale && (
              <div
                style={{
                  fontSize: 12.5,
                  color: "#3f3f46",
                  lineHeight: 1.55,
                  marginBottom: data.recommendation.strong_points?.length ? 8 : 0,
                  background: "rgba(255, 255, 255, 0.5)",
                  borderRadius: 8,
                  padding: "8px 10px",
                  borderLeft: "3px solid #f59e0b",
                }}
              >
                {data.recommendation.rationale}
              </div>
            )}

            {data.recommendation.strong_points?.length > 0 && (
              <div>
                <div
                  style={{
                    fontSize: 11,
                    color: "#a16207",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                    marginBottom: 4,
                  }}
                >
                  Why this bid
                </div>
                <ul
                  className="ai-rec-points"
                  style={{
                    margin: 0,
                    paddingLeft: 18,
                    fontSize: 12.5,
                    color: "#3f3f46",
                    lineHeight: 1.55,
                  }}
                >
                  {data.recommendation.strong_points.map((p: string, i: number) => (
                    <li key={i} style={{ marginBottom: 2 }}>{p}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* No-bids banner — shown when the analysis ran but no bids exist yet */}
      {(!data.recommendation || (data.stats?.n_bids ?? 0) === 0) && (
        <div
          style={{
            background: "#fef3c7",
            border: "1px solid #fde68a",
            borderRadius: 10,
            padding: "12px 14px",
            marginBottom: 16,
            display: "flex",
            gap: 10,
            alignItems: "flex-start",
          }}
        >
          <BulbFilled style={{ color: "#a16207", fontSize: 18, marginTop: 2 }} />
          <div style={{ fontSize: 13, color: "#78350f", lineHeight: 1.5 }}>
            <strong>No bids submitted yet.</strong> The AI couldn't recommend a
            bid because no suppliers have responded to this tender. See the
            "Full Report" tab below for the AI's narrative on what to do next.
          </div>
        </div>
      )}

      {/* Inner tabs */}
      <Tabs
        className="ai-insight-tabs"
        activeKey={innerTab}
        onChange={setInnerTab}
        items={innerTabs.map((t: any) => ({
          ...t,
          children: (
            <TabErrorBoundary label={typeof t.label === "string" ? t.label : undefined}>
              {t.children}
            </TabErrorBoundary>
          ),
        }))}
        tabBarStyle={{ marginBottom: 12 }}
      />

      {/* Full-screen report overlay — portaled to body so it covers the
          top nav and side menu, not just this component's box. */}
      {reportFullscreen &&
        createPortal(
          <div
            role="dialog"
            aria-modal="true"
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 9999,
              background: "#f8fafc",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Overlay header */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: "12px 24px",
                background: "#fff",
                borderBottom: "1px solid #e6e8eb",
                boxShadow: "0 2px 6px rgba(15, 23, 42, 0.04)",
                flexShrink: 0,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                <FileMarkdownOutlined
                  style={{ color: "#2263b3", fontSize: 18, flexShrink: 0 }}
                />
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 15,
                      fontWeight: 700,
                      color: "#0f172a",
                      lineHeight: 1.2,
                    }}
                  >
                    AI Evaluation Report
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: "#64748b",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {data.header?.tender_id || data.tender_id}
                    {data.header?.tender_title ? ` — ${data.header.tender_title}` : ""}
                    {data.version != null ? ` · v${data.version}` : ""}
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <Tooltip title="Save the report as a PDF">
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={handleDownloadPdf}
                    loading={pdfDownloading}
                    disabled={!data.content_md}
                    style={{ borderRadius: 8 }}
                  >
                    Download PDF
                  </Button>
                </Tooltip>
                <Tooltip title="Exit full screen (Esc)">
                  <Button
                    type="default"
                    icon={<FullscreenExitOutlined />}
                    onClick={() => setReportFullscreen(false)}
                    style={{ borderRadius: 8 }}
                  >
                    Exit Full Screen
                  </Button>
                </Tooltip>
              </div>
            </div>

            {/* Overlay body — scrollable */}
            <div
              style={{
                flex: 1,
                overflowY: "auto",
                padding: "24px 32px 40px",
              }}
            >
              <div
                ref={fullscreenReportRef}
                style={{
                  maxWidth: 920,
                  margin: "0 auto",
                  background: "#fff",
                  border: "1px solid #eef0f3",
                  borderRadius: 12,
                  padding: "28px 40px",
                  boxShadow: "0 4px 24px rgba(15, 23, 42, 0.06)",
                  fontSize: 14.5,
                  lineHeight: 1.65,
                }}
              >
                {data.content_md ? (
                  <MarkdownMessage text={data.content_md} />
                ) : (
                  <Empty />
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
    </DocViewerProvider>
  );
};

// ---- Local style helpers ----
const statBox = (bg: string, border: string, flex?: string): React.CSSProperties => ({
  background: bg,
  border: `1px solid ${border}`,
  borderRadius: 8,
  padding: "10px 14px",
  flex,
});
const statLabel: React.CSSProperties = { fontSize: 11, color: "#64748b", fontWeight: 500 };
const statValue: React.CSSProperties = { fontSize: 18, fontWeight: 700, color: "#0f172a", marginTop: 2 };
const sectionHeading: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 700,
  color: "#1e3a8a",
  marginTop: 4,
  marginBottom: 10,
};
const subHeading = (color: string): React.CSSProperties => ({
  fontSize: 12,
  fontWeight: 700,
  color,
  marginBottom: 4,
});
const bulletList: React.CSSProperties = {
  margin: 0,
  paddingLeft: 18,
  fontSize: 12.5,
  color: "#475569",
  lineHeight: 1.55,
};
const matrixTh = (highlight = false): React.CSSProperties => ({
  border: "1px solid #eef0f3",
  borderTop: "none",
  background: highlight ? "#eff6ff" : "#f8fafc",
  padding: "8px 10px",
  textAlign: "left",
  fontSize: 12,
  color: "#1e3a8a",
  fontWeight: 600,
});
const matrixTd = (highlight = false): React.CSSProperties => ({
  border: "1px solid #eef0f3",
  background: highlight ? "#f8fbff" : "#fff",
  padding: "8px 10px",
  fontSize: 12,
  color: "#334155",
});

export default AIInsightView;
